import React, { FC, useMemo } from 'react';
import { Slice, VictoryPie, VictoryTooltip } from 'victory';

import Styled from './styles';
import colors from '../../assets/colors';
import useWindowSize from '../../core/hooks/useWindowSize';
import { fontFamily } from '../../assets/themes';
import { kFormatter } from '../../core/helpers/numberFormatter';

const pieColors = [
	'#FFC700',
	'#FF5017',
	'#180B66',
	'#4AC5EC',
	'#D57EFF',
	'#0038FF',
	'#1DEA57',
	'#C24AEC',
	'#FF00B8',
	'#7EE8FF',
	'#8A1DEA',
	'#FF0039',
	'#00FF07',
	'#288DFF',
	'#FFDC53',
	'#00FFC6',
	'#EA1DE3',
	'#F7FF00',
	'#FF0078',
	'#FF8700',
];

const maxItems = pieColors.length;

const getColor = (i: number) => pieColors[i] || colors.grey;

const PieChart: FC<any> = ({ isDetail = false, data, grouping }) => {
	const { width } = useWindowSize();
	const totalCount = data.reduce((acc: any, item: any) => acc + item.count, 0);
	data = data.sort((a: any, b: any) => b.count - a.count);
	const isMobile = width <= 768;
	const isBig = width >= 1920

	const sections = useMemo(() => {
		return data.reduce((acc: any, item: any, i: number) => {
			const color = getColor(i);
			if (maxItems > i) {
				return [
					...acc,
					{
						name: item._id,
						count: item.count,
						percentage: (item.count / totalCount) * 100,
						color,
					},
				];
			}
			const otherItem = acc.find((x: any) => x.name === 'Other');
			const itemsCount = otherItem ? item.count + otherItem.count : item.count;
			return [
				...acc.filter((x: any) => x.name !== 'Other'),
				{
					name: 'Other',
					count: itemsCount,
					percentage: (itemsCount / totalCount) * 100,
					color,
				},
			];
		}, []);
	}, [data, totalCount]);

	const CustomSlice = (props: any) => {
		const { index, style } = props;
		return (
			<Slice
				{...props}
				style={{ fill: pieColors.length === index ? colors.grey : style.fill }}
			/>
		);
	};

	if (!totalCount) return null;

	return (
		<Styled.Wrapper>
			<Styled.LeftContent>
				<VictoryPie
					x="name"
					y="count"
					height={isMobile ? 350 : 300}
					colorScale={pieColors}
					innerRadius={isMobile ? 90 : 80}
					data={sections}
					style={{
						labels: { fill: colors.black, fontSize: 8 },
					}}
					dataComponent={<CustomSlice />}
					labelComponent={
						<VictoryTooltip
							dy={0}
							dx={0}
							style={{
								fontSize: isBig ? 6.5 : 6.5,
								fill: '#fff',
								fontWeight: 600,
								fontFamily: fontFamily.primaryRegular,
							}}
							cornerRadius={isBig ? 3 : 2.8}
							orientation="top"
							flyoutHeight={isBig ? 13 : 11}
							flyoutPadding={isBig ? 5 : 7}
							pointerLength={isBig ? 3 : 2}
							pointerWidth={isBig ? 6 : 5}
							flyoutStyle={{ fill: '#000' }}
							renderInPortal={false}
						/>
					}
					labels={({ datum }) => `${kFormatter(datum.count, true)}`}
				/>
				<Styled.CenterBox>
					<Styled.Title color="black" text={String(totalCount)} type="bold" />
				</Styled.CenterBox>
			</Styled.LeftContent>
			<Styled.RightContent  style={{ display: 'flex'}}>
				<div
					style={{
					position: 'absolute',
					fontSize: '14px',
					fontFamily: fontFamily.primaryRegular,
					margin: '-30px 0px 0px 0px',
					}}
				>
					<span> {grouping?.groupingLevel1} </span>
				</div>
				{sections.map((item: any, i: number) => (
					<Styled.Item key={i}>
						<Styled.Circle
							style={{
								backgroundColor: item.color,
							}}
						/>
						<Styled.Descr color="black" size="normal">
							{`${item.name} - ${item.count}`}
						</Styled.Descr>
					</Styled.Item>
				))}
			</Styled.RightContent>
		</Styled.Wrapper>
	);
};

export default PieChart;
